import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { Ban, ChevronDown, RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import { cancelJob, jobStatsQuery, jobsQuery, retryJob, type JobLane, type JobStatus } from '@/api/jobs'
import { keys } from '@/api/keys'
import { JobModelLabel } from '@/components/JobModelLabel'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { formatClock, formatDuration, formatRelative, formatUsd, humanizeJobType } from '@/lib/format'
import type { Job } from '@/schema'
import { newerJob, useQueueStore } from '@/stores/queueStore'

/** `/jobs?status=&type=&lane=` — the last 200 jobs with envelopes, retry/cancel. */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const jobsSearchSchema = z.object({
  status: lenient(z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled'])),
  type: lenient(z.string()),
  lane: lenient(z.enum(['io', 'cpu', 'ai_interactive', 'ai_bulk'])),
})

export type JobsSearch = z.infer<typeof jobsSearchSchema>

export function validateJobsSearch(input: Record<string, unknown>): JobsSearch {
  return jobsSearchSchema.parse(input)
}

const route = getRouteApi('/jobs')

const STATUSES: JobStatus[] = ['queued', 'running', 'succeeded', 'failed', 'cancelled']
const LANES: { value: JobLane; label: string }[] = [
  { value: 'io', label: 'Downloads' },
  { value: 'cpu', label: 'Pictures' },
  { value: 'ai_interactive', label: 'Stories (AI)' },
  { value: 'ai_bulk', label: 'Bulk AI' },
]

const STATUS_TONE: Record<JobStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'accent',
  succeeded: 'ok',
  failed: 'danger',
  cancelled: 'warn',
}

/**
 * Every job the studio has run, newest first.
 *
 * The list is the server's (`GET /api/jobs`, last 200) with the live rows
 * from `queueStore` folded on top, so a job that started one second ago is
 * already here without waiting for a refetch. Each row opens to show what
 * the job was actually given and what came back — the thing you need when a
 * Claude call failed and the message alone is not enough.
 */
export function JobsPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const qc = useQueryClient()

  const listQuery = useQuery({ ...jobsQuery({ ...search, limit: 200 }), refetchInterval: 15_000 })
  const stats = useQuery(jobStatsQuery)
  const liveJobs = useQueueStore((s) => s.jobs)

  const jobs = useMemo(() => {
    const byId = new Map<number, Job>()
    for (const j of listQuery.data?.items ?? []) byId.set(j.id, j)
    for (const j of liveJobs) {
      byId.set(j.id, newerJob(byId.get(j.id), j))
    }
    return [...byId.values()].filter((j) => matches(j, search)).sort((a, b) => b.id - a.id)
  }, [listQuery.data, liveJobs, search])

  const setSearch = (patch: Partial<JobsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  }

  const s = stats.data

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Jobs</h1>
          <p className="text-sm text-ink-2">Indexing, pictures, AI calls, story generation and narration.</p>
        </div>
        {s ? (
          <dl className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <Stat label="Waiting" value={String(sum(s.by_lane, 'queued'))} />
            <Stat label="Running" value={String(sum(s.by_lane, 'running'))} />
            <Stat label="Spent today" value={formatUsd(s.spent_today_usd)} />
            {s.oldest_queued_age_s ? <Stat label="Oldest wait" value={formatDuration(s.oldest_queued_age_s)} /> : null}
          </dl>
        ) : null}
      </header>

      {s?.paused_until ? (
        <p className="rounded-xl border border-warn bg-warn-soft px-3 py-2 text-sm text-ink" role="status">
          AI work is paused until {formatClock(s.paused_until)}
          {s.paused_for === 'usage_window' ? ' — the usage window is full.' : s.paused_for === 'rate_limit' ? ' — the service asked us to slow down.' : '.'} Everything
          queued will carry on by itself.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Chip selected={!search.status} onClick={() => setSearch({ status: undefined })}>
          All
        </Chip>
        {STATUSES.map((st) => (
          <Chip key={st} selected={search.status === st} onClick={() => setSearch({ status: st })}>
            {st}
          </Chip>
        ))}
        <span className="mx-1 h-6 w-px bg-line" aria-hidden />
        <Chip selected={!search.lane} onClick={() => setSearch({ lane: undefined })}>
          Every lane
        </Chip>
        {LANES.map((l) => (
          <Chip key={l.value} selected={search.lane === l.value} onClick={() => setSearch({ lane: l.value })}>
            {l.label}
            {s?.by_lane?.[l.value] ? ` (${s.by_lane[l.value].queued + s.by_lane[l.value].running})` : ''}
          </Chip>
        ))}
        {search.type ? (
          <Chip selected onRemove={() => setSearch({ type: undefined })}>
            {humanizeJobType(search.type)}
          </Chip>
        ) : null}
      </div>

      {listQuery.isError ? (
        <div className="card px-6 py-12 text-center" role="alert">
          <p className="text-sm text-ink-2">Could not load the job list. {listQuery.error instanceof Error ? listQuery.error.message : ''}</p>
          <Button className="mt-3" onClick={() => void listQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : jobs.length === 0 ? (
        <div className="card px-6 py-16 text-center">
          <p className="text-sm font-medium text-ink">{listQuery.isPending ? 'Loading…' : 'No jobs here'}</p>
          {!listQuery.isPending ? <p className="mt-1 text-xs text-ink-3">Index the bucket or start a story and the work will show up here.</p> : null}
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="jobs-list">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onFilterType={(t) => setSearch({ type: t })} onChanged={() => void qc.invalidateQueries({ queryKey: keys.jobs.all() })} />
          ))}
        </ul>
      )}
    </div>
  )
}

function matches(job: Job, search: JobsSearch): boolean {
  if (search.status && job.status !== search.status) return false
  if (search.lane && job.lane !== search.lane) return false
  if (search.type && job.type !== search.type) return false
  return true
}

function sum(byLane: Record<string, { queued: number; running: number }> | undefined, key: 'queued' | 'running'): number {
  return Object.values(byLane ?? {}).reduce((n, l) => n + (l?.[key] ?? 0), 0)
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className="font-semibold text-ink tabular-nums">{value}</dd>
    </div>
  )
}

function JobCard({ job, onFilterType, onChanged }: { job: Job; onFilterType(type: string): void; onChanged(): void }) {
  const [open, setOpen] = useState(false)
  const upsert = useQueueStore((s) => s.upsertJob)

  const cancel = useMutation({
    mutationFn: () => cancelJob(job.id),
    onSuccess: (j) => {
      upsert(j)
      onChanged()
      toast.info(`${j.status === 'cancelled' ? 'Cancelled' : j.cancel_requested ? 'Cancelling' : 'Already finished'} #${job.id}`)
    },
    onError: (e) => toast.error('Could not cancel', { description: e instanceof Error ? e.message : String(e) }),
  })
  const retry = useMutation({
    mutationFn: () => retryJob(job.id),
    onSuccess: (j) => {
      upsert(j)
      onChanged()
      toast.success(`Re-queued #${job.id}`)
    },
    onError: (e) => toast.error('Could not retry', { description: e instanceof Error ? e.message : String(e) }),
  })

  const active = job.status === 'queued' || job.status === 'running'
  const cancelling = active && (cancel.isPending || job.cancel_requested)
  const pct = job.progress?.pct
  const phase = job.progress?.msg ?? job.progress?.phase ?? null

  return (
    <li className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? `Hide details of job ${job.id}` : `Show details of job ${job.id}`}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-2 hover:bg-paper-2 focus-visible:focus-ring"
        >
          <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>

        <span className="font-mono text-xs text-ink-3">#{job.id}</span>

        <button type="button" className="truncate text-sm font-medium text-ink hover:underline" onClick={() => onFilterType(job.type)} title={job.type}>
          {humanizeJobType(job.type)}
        </button>

        <Badge tone={STATUS_TONE[job.status]} dot>
          {cancelling ? 'cancelling' : job.status}
        </Badge>
        {job.parent_job_id ? <Badge tone="neutral">part of #{job.parent_job_id}</Badge> : null}
        {job.children ? (
          <Badge tone="neutral">
            {job.children.done}/{job.children.total}
            {job.children.failed ? ` · ${job.children.failed} failed` : ''}
          </Badge>
        ) : null}

        <span className="ml-auto flex items-center gap-3 text-xs text-ink-3">
          {job.cost_usd != null ? <span>{formatUsd(job.cost_usd)}</span> : null}
          <span>{formatRelative(job.finished_at ?? job.started_at ?? job.created_at)}</span>
        </span>

        {active ? (
          <Button size="sm" variant="ghost" onClick={() => cancel.mutate()} loading={cancelling} disabled={cancelling}>
            <Ban aria-hidden />
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        ) : job.status === 'failed' || job.status === 'cancelled' ? (
          <Button size="sm" variant="ghost" onClick={() => retry.mutate()} loading={retry.isPending}>
            <RotateCcw aria-hidden />
            Retry
          </Button>
        ) : null}
      </div>

      {job.progress.data?.ai_model ? <div className="px-4 pb-2"><JobModelLabel job={job} /></div> : null}
      {cancel.isError ? <p role="alert" className="px-4 pb-3 text-xs text-danger">Could not cancel. {cancel.error.message}</p> : null}

      {phase || job.error ? (
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 text-xs">
          {phase ? <span className="text-ink-2" role={cancelling ? 'status' : undefined}>{cancelling ? 'Stopping this job…' : phase}</span> : null}
          {job.error ? <span className="text-danger">{job.error}</span> : null}
        </div>
      ) : null}

      {active && pct != null ? (
        <div className="mx-4 mb-3 h-1.5 overflow-hidden rounded-full bg-paper-3" role="progressbar" aria-label={`Job ${job.id} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
          <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${Math.round(pct * 100)}%` }} />
        </div>
      ) : null}

      {open ? (
        <div className="border-t border-line bg-paper-2 px-4 py-3 text-xs">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
            <Detail label="Lane" value={job.lane} />
            <Detail label="Priority" value={String(job.priority)} />
            <Detail label="Attempts" value={`${job.attempts} of ${job.max_attempts}`} />
            <Detail label="Started by" value={job.created_by ?? 'studio'} />
            <Detail label="Created" value={formatClock(job.created_at)} />
            {job.started_at ? <Detail label="Started" value={formatClock(job.started_at)} /> : null}
            {job.finished_at ? <Detail label="Finished" value={formatClock(job.finished_at)} /> : null}
            {job.run_after ? <Detail label="Waits until" value={formatClock(job.run_after)} /> : null}
          </dl>

          <Envelope title="What it was given" value={job.params} />
          {job.result ? <Envelope title="What came back" value={job.result} /> : null}
        </div>
      ) : null}
    </li>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-3">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
    </div>
  )
}

function Envelope({ title, value }: { title: string; value: Record<string, unknown> }) {
  if (!value || Object.keys(value).length === 0) return null
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-ink-2 hover:text-ink">{title}</summary>
      <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-line bg-paper p-2 font-mono text-[11px] leading-relaxed text-ink-2">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  )
}
